import { ChevronRightIcon } from 'lucide-react';
import * as React from 'react';

import { DimeButton } from '../DimeButton.js';
import { DimeCheckbox } from '../DimeCheckbox.js';
import { cn } from '../lib/utils.js';
import type { DimeTreeComboboxRow } from './tree-combobox-model.js';

const INDENT_PX = 20;

type TreeComboboxRowProps = {
  row: DimeTreeComboboxRow;
  domId: string;
  isActive: boolean;
  isChecked: boolean;
  /** Label of the checked ancestor that includes this row, if any. */
  includedWith: string | undefined;
  isMuted: boolean;
  isSearching: boolean;
  trailing: React.ReactNode;
  onToggleExpanded: (id: string) => void;
  onToggleSelected: (id: string) => void;
};

// Keeps focus in the search input, which owns the keyboard through aria-activedescendant.
const keepInputFocus = (event: React.MouseEvent) => event.preventDefault();

export function TreeComboboxRow({
  row,
  domId,
  isActive,
  isChecked,
  includedWith,
  isMuted,
  isSearching,
  trailing,
  onToggleExpanded,
  onToggleSelected,
}: Readonly<TreeComboboxRowProps>) {
  const { node } = row;
  const isIncluded = includedWith !== undefined;
  const labelId = `${domId}-label`;
  const descriptionId = `${domId}-description`;
  const trailingId = `${domId}-trailing`;
  const hasTrailing = (trailing ?? null) !== null;
  const describedBy =
    [hasTrailing ? trailingId : null, isIncluded ? descriptionId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- keys reach rows through the search input's aria-activedescendant, so the row itself never takes focus.
    <div
      id={domId}
      role="treeitem"
      tabIndex={-1}
      aria-labelledby={labelId}
      aria-describedby={describedBy}
      aria-level={row.depth + 1}
      aria-setsize={row.setSize}
      aria-posinset={row.posInSet}
      // While searching, expansion is fixed, so a parent whose children do not match never reports as collapsed.
      aria-expanded={row.canExpand && (!isSearching || row.isExpanded) ? row.isExpanded : undefined}
      aria-selected={isChecked || isIncluded}
      aria-disabled={isIncluded || undefined}
      data-active={isActive || undefined}
      className={cn(
        'flex min-h-8 cursor-pointer items-center gap-1.5 rounded-md pr-2 text-sm select-none data-active:bg-accent data-active:text-accent-foreground aria-disabled:cursor-default',
        // Forced colors drops the accent background, so the active row is outlined in Highlight instead.
        'forced-colors:data-active:outline-2 forced-colors:data-active:-outline-offset-2 forced-colors:data-active:outline-[Highlight]',
      )}
      style={{ paddingInlineStart: `${row.depth * INDENT_PX}px` }}
      onMouseDown={keepInputFocus}
      onClick={() => onToggleSelected(node.id)}
    >
      <TreeComboboxChevron
        row={row}
        isSearching={isSearching}
        onToggleExpanded={onToggleExpanded}
      />
      <DimeCheckbox
        checked={isChecked || isIncluded}
        disabled={isIncluded}
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none"
      />
      <span
        id={labelId}
        className={cn('min-w-0 flex-1 truncate', isMuted && 'text-muted-foreground')}
      >
        {node.label}
      </span>
      {hasTrailing && (
        <span id={trailingId} className="shrink-0 text-xs text-muted-foreground tabular-nums">
          {trailing}
        </span>
      )}
      {isIncluded && (
        <span id={descriptionId} className="sr-only">
          {`Included with ${includedWith}`}
        </span>
      )}
    </div>
  );
}

function TreeComboboxChevron({
  row,
  isSearching,
  onToggleExpanded,
}: Readonly<{
  row: DimeTreeComboboxRow;
  isSearching: boolean;
  onToggleExpanded: (id: string) => void;
}>) {
  if (!row.canExpand || isSearching) return <span aria-hidden="true" className="size-6 shrink-0" />;
  const { node } = row;

  return (
    <DimeButton
      type="button"
      variant="ghost"
      size="icon-xs"
      tabIndex={-1}
      aria-label={`${row.isExpanded ? 'Collapse' : 'Expand'} ${node.label}`}
      className="shrink-0"
      onMouseDown={keepInputFocus}
      onClick={(event) => {
        // The chevron only expands; it must never also toggle the row's check.
        event.stopPropagation();
        onToggleExpanded(node.id);
      }}
    >
      <ChevronRightIcon
        aria-hidden="true"
        className={cn('transition-transform', row.isExpanded && 'rotate-90')}
      />
    </DimeButton>
  );
}
